import { FormEvent, useEffect, useRef } from 'react'
import { UseBpmRangeProps, UseTempoProps } from './TempoBpm.types'
import { useInstantSearch, useMenu, useRange } from 'react-instantsearch'

import { useTranslation } from 'next-i18next'

export const BPM_LIMIT = 300

export const useTempo = (): UseTempoProps => {
	const { t } = useTranslation(['search'])

	const { indexUiState } = useInstantSearch()

	const attribute = indexUiState.configure?.restrictSearchableAttributes?.find(
		(item) => item.startsWith('TempoName_')
	)

	const { items, refine } = useMenu({
		attribute: attribute ?? 'TempoName',
	})

	const tempos = [
		{ value: t('very-slow') },
		{ value: t('slow') },
		{ value: t('medium') },
		{ value: t('fast') },
		{ value: t('very-fast') },
	].map((tempo) => ({
		...tempo,
		...items?.find((item) => item.label === tempo.value),
	}))

	const handleTempoChange = (event: React.FormEvent<HTMLDivElement>): void => {
		const { value } = event.target as HTMLButtonElement

		refine(value)
	}

	return {
		tempos,
		handleTempoChange,
	}
}

export const useBpmRange = (): UseBpmRangeProps => {
	const openingBpmMinRef = useRef()
	const openingBpmMaxRef = useRef()
	const closingBpmMinRef = useRef()
	const closingBpmMaxRef = useRef()

	const OPENING_BPM_MIN = 'OPENING_BPM_MIN'
	const OPENING_BPM_MAX = 'OPENING_BPM_MAX'
	const CLOSING_BPM_MIN = 'CLOSING_BPM_MIN'
	const CLOSING_BPM_MAX = 'CLOSING_BPM_MAX'

	const { indexUiState, setIndexUiState } = useInstantSearch()

	const { range: openingBpmRange, start: openingBpmStart } = useRange({
		attribute: 'TempoStart',
	})

	const { range: closingBpmRange, start: closingBpmStart } = useRange({
		attribute: 'TempoFinish',
	})

	const { min: openingBpmMin, max: openingBpmMax } = openingBpmRange
	const { min: closingBpmMin, max: closingBpmMax } = closingBpmRange

	const refineOpeningBpm = (start: number[]): void => {
		setIndexUiState((prevState) => ({
			...prevState,
			range: {
				...prevState.range,
				TempoStart: `${start[0]}:${start[1]}`,
			},
		}))
	}

	const refineClosingBpm = (start: number[]): void => {
		setIndexUiState((prevState) => ({
			...prevState,
			range: {
				...prevState.range,
				TempoFinish: `${start[0]}:${start[1]}`,
			},
		}))
	}

	const handleBpmInput = (event: FormEvent): void => {
		const { name } = event.target as HTMLInputElement

		const value: number = parseInt((event.target as HTMLInputElement).value)

		if (name === OPENING_BPM_MIN) {
			const min = Number.isNaN(value) ? -Infinity : value

			const openingBpmMaxInput = openingBpmMaxRef?.current as HTMLInputElement

			const max = parseInt(openingBpmMaxInput.value, 10)

			if (Number.isNaN(min) && Number.isNaN(max)) {
				refineClosingBpm([-Infinity, Infinity])

				return
			}

			if (!Number.isNaN(min) && (Number.isNaN(max) || min > max)) {
				openingBpmMaxInput.value = min.toString()

				refineOpeningBpm([min, min])

				return
			}

			refineOpeningBpm([min, max])
		}

		if (name === OPENING_BPM_MAX) {
			const openingBpmMinInput = openingBpmMinRef?.current as HTMLInputElement

			const min = openingBpmMinInput.value
				? parseInt(openingBpmMinInput.value, 10)
				: -Infinity

			const max = Number.isNaN(value) ? Infinity : value

			refineOpeningBpm([min, max])
		}

		if (name === CLOSING_BPM_MIN) {
			const min = Number.isNaN(value) ? -Infinity : value

			const closingBpmMaxInput = closingBpmMaxRef?.current as HTMLInputElement

			const max = parseInt(closingBpmMaxInput.value, 10)

			if (Number.isNaN(min) && Number.isNaN(max)) {
				refineClosingBpm([-Infinity, Infinity])

				return
			}

			if (!Number.isNaN(min) && (Number.isNaN(max) || min > max)) {
				closingBpmMaxInput.value = min.toString()

				refineClosingBpm([min, min])

				return
			}

			refineClosingBpm([min, max])
		}

		if (name === CLOSING_BPM_MAX) {
			const closingBpmMinInput = closingBpmMinRef?.current as HTMLInputElement

			const min = closingBpmMinInput.value
				? parseInt(closingBpmMinInput.value, 10)
				: -Infinity

			const max = Number.isNaN(value) ? Infinity : value

			refineClosingBpm([min, max])
		}
	}

	const defaultOpeningBpmMin =
		Number.isFinite(openingBpmStart[0]) && openingBpmStart[0]
	const defaultOpeningBpmMax =
		Number.isFinite(openingBpmStart[1]) && openingBpmStart[1]
	const defaultClosingBpmMin =
		Number.isFinite(closingBpmStart[0]) && closingBpmStart[0]
	const defaultClosingBpmMax =
		Number.isFinite(closingBpmStart[1]) && closingBpmStart[1]

	useEffect(() => {
		if (!indexUiState.range?.TempoStart) {
			const openingBpmMinInput = openingBpmMinRef?.current as HTMLInputElement
			const openingBpmMaxInput = openingBpmMaxRef?.current as HTMLInputElement

			openingBpmMinInput.value = ''
			openingBpmMaxInput.value = ''
		}
	}, [indexUiState.range?.TempoStart, openingBpmRange.max, openingBpmRange.min])

	useEffect(() => {
		if (!indexUiState.range?.TempoFinish) {
			const closingBpmMinInput = closingBpmMinRef?.current as HTMLInputElement
			const closingBpmMaxInput = closingBpmMaxRef?.current as HTMLInputElement

			closingBpmMinInput.value = ''
			closingBpmMaxInput.value = ''
		}
	}, [closingBpmRange, closingBpmStart, indexUiState.range?.TempoFinish])

	return {
		openingBpmMinRef,
		openingBpmMaxRef,
		closingBpmMinRef,
		closingBpmMaxRef,
		openingBpmMin,
		openingBpmMax,
		closingBpmMin,
		closingBpmMax,
		handleBpmInput,
		OPENING_BPM_MIN,
		OPENING_BPM_MAX,
		CLOSING_BPM_MIN,
		CLOSING_BPM_MAX,
		openingBpmStart,
		closingBpmStart,
		defaultOpeningBpmMin,
		defaultOpeningBpmMax,
		defaultClosingBpmMin,
		defaultClosingBpmMax,
	}
}
